import React from 'react';
import {
  Table,
  Col,
  Input,
  Card,
  CardBody,
  Spinner,
  CardFooter,
  Button,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import Icon from '@mdi/react';
import { mdiAccountOutline } from '@mdi/js';
import {
  travelRequestHeaders,
  bookingCategoryOptions,
  renderStatusColors,
} from './constants';
import { useTableHook } from '../../../components/hooks';
import {
  fetchTravelBookingAgent,
  fetchTravelRequestEnquiry,
  fetchTravelRequestEnquiryFilter,
} from '../../../store/apis/travelService';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  Badge,
  UserAsyncAutocompleteSelect,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { fetchEmployeesAutoComplete } from '../../../store/apis/autocompleteService';
import { AuthUtil } from '../../../utils';
import { Messages } from '../../../common/messages';
import './HRTravelEnquiry.scss';
import { withBase } from '../../../utils/route.util';

function HRTravelEnquiry() {
  document.title = 'HR Travel Request Enquiry | Envision - Daikin';

  const initialState = {
    travelSearchType: 'TRAVEL_SEARCH',
    travelReqNo: '',
    travelStatus: '',
    bookingAgentCode: '',
    bookingCategory: '',
    travelFromDate: '',
    travelToDate: '',
    travelType: '',
    employeeId: '',
  };

  // Table Hook
  const {
    data,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    resetFilters,
    columnData,
    setColumnName,
    columnName,
    applyFilter,
    handleManualSort,
    handleSetExternalFilters,
    clearSingleFilterValue,
    checkColumnIfSorted,
    nextPage,
    prevPage,
    isLoadingData,
    isLoadingColumn,
    applyExternalFilters,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'RequestNo',
      sortDirection: 'DESC',
      filters: [],
      travelSearchType: 'TRAVEL_SEARCH',
      tableName: 'TravelEnquiry',
      // Filters
      travelReqNo: '',
      travelStatus: '',
      // employeeId: '',
      employeeId: '',
      bookingAgentCode: '',
      bookingCategory: '',
      travelFromDate: '',
      travelToDate: '',
      travelType: '',
    },
    fetchFn: fetchTravelRequestEnquiry,
    fetchColumnFn: fetchTableColumnValues,
    columnKeyPrefix: 'travelEnquiryColumn',
    queryKeyPrefix: 'travelEnquiry',
    tableName: 'TravelEnquiry',
    defaultExternalFilters: {
      ...initialState,
    },
  });

  const { data: travelStatusOptions } = useQuery({
    queryKey: ['travelStatusOptions'],
    queryFn: () =>
      fetchTravelRequestEnquiryFilter({
        codeType: 'TRAVEL_STATUS',
      }),
  });

  const { data: travelAgentOptions } = useQuery({
    queryKey: ['travelAgentOptions'],
    queryFn: () => fetchTravelBookingAgent(),
  });

  const { data: travelTypeOptions } = useQuery({
    queryKey: ['travelTypeOptions'],
    queryFn: () =>
      fetchTravelRequestEnquiryFilter({
        codeType: 'TRAVEL_TYPE',
      }),
  });

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={6}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_TRAVEL
            )}
          </td>
        </tr>
      );
    }

    return tableData?.map(item => (
      <tr
        key={item?.RequestNo}
        className="cursor-pointer"
        onClick={() => {
          window.open(
            withBase(
              `/hr/travel-request/edit/${item?.RequestNo}/${item?.RevisionNo}?m=View`,
            ),
            '_blank',
          );
        }}
      >
        <td>
          <button
            type="button"
            className="btn btn-link p-0 text-decoration-underline"
            onClick={() => {
              window.open(
                withBase(
                  `/hr/travel-request/edit/${item?.RequestNo}/${item?.RevisionNo}?m=View`,
                ),
                '_blank',
              );
            }}
          >
            {item?.RequestNo}
          </button>
        </td>
        <td>{item?.TravelDate}</td>
        <td>{item?.UserFullName}</td>
        <td>{item?.TravelDestination}</td>
        <td
          style={{
            maxWidth: '400px',
            textOverflow: 'ellipsis',
            overflowX: 'hidden',
          }}
        >
          {item?.TravelPurpouse}
        </td>
        <td>
          <Badge config={renderStatusColors[item.Status]}>{item.Status}</Badge>
        </td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.travelFromDate &&
      externalFilters.travelToDate &&
      new Date(externalFilters?.travelFromDate) >
        new Date(externalFilters?.travelToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">Travel Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col lg={12}>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-row gap-2">
                  <Col className="external-filter-col">
                    <b>Request Number</b>
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control external-filter-input"
                        placeholder="Enter Request Number"
                        onChange={e =>
                          setExternalFilters(prevFilters => ({
                            ...prevFilters,
                            travelReqNo: e.target.value,
                          }))
                        }
                        value={externalFilters?.travelReqNo || ''}
                        onKeyDown={e => {
                          if (e.key === 'Enter') applyExternalFilters();
                        }}
                      />
                      <span className="search-icon ri-search-line" />
                    </div>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Employee</b>
                    <UserAsyncAutocompleteSelect
                      autoCompleteFn={input =>
                        fetchEmployeesAutoComplete({
                          prefixText: input,
                          type: 'EMPLOYEE',
                          hrCompanyCode: AuthUtil.getAuthUser()?.hrCompanyCode,
                        })
                      }
                      mappedFn={rawData =>
                        rawData.map(item => ({
                          value: item.Cd,
                          label: item.Name,
                          name: item.Name,
                          code: item.Cd,
                          designation: item.Designation,
                          image: item?.Imagefile,
                        }))
                      }
                      onChange={e => {
                        handleSetExternalFilters('employeeId', e);
                      }}
                      placeholder="Search by Employee Name & ID"
                      isClearable
                      isSearchable
                      inputIcon={
                        <Icon
                          path={mdiAccountOutline}
                          size={0.8}
                          color="#667085"
                        />
                      }
                      rightKeyAssessor="code"
                      value={externalFilters?.employeeId || ''}
                    />
                  </Col>
                  <Col className="external-filter-col">
                    <b>Travel From Date</b>
                    <DatePicker
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          travelFromDate: e,
                        }))
                      }
                      value={externalFilters?.travelFromDate || ''}
                      className="form-control external-filter-input"
                    />
                  </Col>
                  <Col className="external-filter-col">
                    <b>Travel To Date</b>
                    <DatePicker
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          travelToDate: e,
                        }))
                      }
                      value={externalFilters?.travelToDate || ''}
                      className="form-control external-filter-input"
                    />
                  </Col>
                </div>
                <div className="d-flex flex-row gap-2">
                  <Col className="external-filter-col">
                    <b>Travel Status</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          travelStatus: e.target.value,
                        }))
                      }
                      value={externalFilters?.travelStatus || ''}
                    >
                      <option value="">Select Travel Status</option>
                      {travelStatusOptions?.map(option => (
                        <option key={option?.Code} value={option?.Code}>
                          {option?.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Booking Category</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          bookingCategory: e.target.value,
                        }))
                      }
                      value={externalFilters?.bookingCategory || ''}
                    >
                      <option value="">Select Booking Category</option>
                      {bookingCategoryOptions?.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Booking Agent</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          bookingAgentCode: e.target.value,
                        }))
                      }
                      value={externalFilters?.bookingAgentCode || ''}
                    >
                      <option value="">Select Booking Agent</option>
                      {travelAgentOptions?.map(option => (
                        <option key={option?.Code} value={option?.Code}>
                          {option?.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col className="external-filter-col">
                    <b>Travel Type</b>
                    <Input
                      type="select"
                      className="form-select external-filter-input"
                      onChange={e =>
                        setExternalFilters(prevFilters => ({
                          ...prevFilters,
                          travelType: e.target.value,
                        }))
                      }
                      value={externalFilters?.travelType || ''}
                    >
                      <option value="">Select Type of Travel</option>
                      {travelTypeOptions?.map(option => (
                        <option key={option?.Code} value={option?.Code}>
                          {option?.Description}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </div>
                <div className="d-flex flex-row gap-2">
                  <Col className="d-flex justify-content-center gap-2">
                    <Button
                      type="button"
                      className="custom-light-btn"
                      onClick={() => {
                        applyExternalFilters({
                          ...initialState,
                        });
                        setExternalFilters(prev => ({
                          ...prev,
                          ...initialState,
                        }));
                      }}
                    >
                      Clear
                    </Button>
                    <div className="external-filter-btns gap-2">
                      <Button
                        type="button"
                        className="custom-btn"
                        onClick={() => {
                          if (!externalFiltersValidation()) {
                            return;
                          }
                          applyExternalFilters({
                            ...externalFilters,
                            employeeId:
                              externalFilters?.employeeId?.value || '',
                          });
                        }}
                      >
                        Search
                      </Button>
                    </div>
                  </Col>
                </div>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={travelRequestHeaders}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive">
            <Table
              hover
              className="custom-table align-middle text-nowrap"
              id="hr-tr-table"
            >
              <thead>
                <tr>
                  {Object.entries(travelRequestHeaders).map(([key, value]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />

                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                          isLoading={isLoadingColumn}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </div>
  );
}

export default HRTravelEnquiry;
